/*
  Судебная работа управляющей компании по счёту - состояние и запросы плашки Б1.

  Отдельным запросом, а не полем карточки: карточка открывается сразу, плашка
  приходит следом (тот же приём, что у ленты движения дела). После правки -
  снятой отметки или отмеченных месяцев - карточку просят перечитаться: от
  обеих правок зависят маршрут счёта и сумма к взысканию в её шапке.
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../api/client.js'

/* Таблица дел (В2, 06.10.2026) грузится вместе с плашкой и перечитывается после
   отметки месяцев: от месяцев дела УК зависят строка «не предъявлено» и суммы. */
function load(accountId) {
  return Promise.all([api.customerWork(accountId), api.accountCases(accountId)])
}

/*
  `version` - карточка, к которой относится плашка. Перечитали карточку (завели
  приказ, сменили стадию) - перечитываются и дела: «где сейчас» нашего дела и
  кнопка следующего шага берутся со стадии счёта. Без этого после заведения
  приказа таблица показывала «заявление подано», пока страницу не обновят
  (осмотр в браузере 06.10.2026).
*/
export default function useCustomerWork(accountId, onChanged, version) {
  const [work, setWork] = useState(null)
  const [cases, setCases] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    setError(null)
    load(accountId)
      .then(([loaded, table]) => {
        if (!alive) return
        setWork(loaded)
        setCases(table)
      })
      .catch((failure) => alive && setError(failure))
    return () => {
      alive = false
    }
  }, [accountId, version])

  const changed = useCallback(
    async (next) => {
      setWork(next)
      setCases(await api.accountCases(accountId))
      if (onChanged) onChanged()
    },
    [accountId, onChanged],
  )

  const clearHold = useCallback(
    async (reason) => changed(await api.clearHold(accountId, reason)),
    [accountId, changed],
  )

  const setMonths = useCallback(
    async (caseId, months) => changed(await api.setCaseMonths(accountId, caseId, months)),
    [accountId, changed],
  )

  /* «Суд вернул заявление» (26.10.2026): ответ - уже новая таблица дел; карточку
     просят перечитаться - сменились стадия и сумма к взысканию. */
  const returnFiling = useCallback(
    async (caseId, facts) => {
      setCases(await api.returnFiling(accountId, caseId, facts))
      if (onChanged) onChanged()
    },
    [accountId, onChanged],
  )

  return { work, cases, error, clearHold, setMonths, returnFiling }
}
